import React, { useState } from "react";
import { SOLUTION_CATEGORIES, EMAILS } from "@/lib/content";
import { ShieldCheck, Building2, Factory, Wheat, Bot, BrainCircuit, Cpu, Layers, ArrowRight } from "lucide-react";
import EnquiryForm from "@/components/EnquiryForm";
import EmailStrip from "@/components/EmailStrip";

const ICONS = { Shield: ShieldCheck, Building2, Factory, Wheat, Bot, BrainCircuit, Cpu };

export default function SolutionsPage() {
  const [selectedTech, setSelectedTech] = useState(null);

  const enquireFor = (techTitle) => {
    setSelectedTech({ value: techTitle, nonce: Date.now() });
    const el = document.getElementById("enquiry-section");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="solutions-page">
      {/* HEADER */}
      <section className="vj-dark vj-radial-fade px-6 lg:px-12 py-20">
        <div className="max-w-[1400px]">
          <div className="overline text-slate-400">Solutions & Technologies</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tighter leading-[1.05] mt-4 max-w-4xl">
            Mission-critical technologies engineered for the next era.
          </h1>
          <p className="text-slate-300 mt-6 max-w-3xl leading-relaxed">
            VAJRON Global Tech Pvt. Ltd. develops drone systems, robotics, automation, artificial intelligence, intelligent
            sensing platforms, embedded systems, and mission-critical technologies — scalable, customizable, and built for
            defence, government, enterprise, and research customers worldwide.
          </p>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="px-6 lg:px-12 py-16 max-w-[1400px]">
        <div className="space-y-20">
          {SOLUTION_CATEGORIES.map((cat, idx) => {
            const Icon = ICONS[cat.icon] || Layers;
            return (
              <div key={cat.id} className="grid lg:grid-cols-12 gap-10" data-testid={`solution-section-${cat.id}`}>
                <div className="lg:col-span-4">
                  <div className="overline">{cat.overline}</div>
                  <div className="mt-3 h-12 w-12 border border-slate-200 flex items-center justify-center text-[#1E3A8A]">
                    <Icon size={22} strokeWidth={1.5}/>
                  </div>
                  <h2 className="font-display font-extrabold text-2xl lg:text-3xl tracking-tight mt-5">{cat.title}</h2>
                  <p className="text-slate-600 mt-4 leading-relaxed text-[15px]">{cat.description}</p>
                </div>
                <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {cat.items.map((it) => (
                    <button
                      type="button"
                      key={it.title}
                      onClick={() => enquireFor(it.title)}
                      className="vj-card p-5 text-left group hover:border-[#1E3A8A] transition-colors"
                      data-testid={`solution-item-${cat.id}-${it.title.toLowerCase().replace(/[^a-z0-9]+/g,'-').slice(0,40)}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-1 h-1.5 w-6 bg-[#1E3A8A]" />
                        <div className="flex-1">
                          <h3 className="font-display font-bold text-[15px] leading-snug">{it.title}</h3>
                          <p className="text-sm text-slate-600 mt-2 leading-relaxed">{it.desc}</p>
                          <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#1E3A8A] opacity-0 group-hover:opacity-100 transition-opacity">
                            Send Enquiry <ArrowRight size={12}/>
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ENQUIRY */}
      <section id="enquiry-section" className="px-6 lg:px-12 py-16 bg-slate-50 border-t border-slate-200" data-testid="solutions-enquiry-section">
        <div className="max-w-[1400px] grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="overline">Enquiries</div>
            <h2 className="font-display text-3xl font-extrabold mt-3 tracking-tight">Request a technology, system, or custom solution.</h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Select any technology above to enquire about it directly, or send us a custom enquiry for a requirement
              not listed here. Our engineering team reviews every enquiry.
            </p>
          </div>
          <div className="lg:col-span-8">
            <EnquiryForm selectedTechnology={selectedTech?.value} />
          </div>
        </div>
      </section>

      <EmailStrip
        heading="Need technical details or solution support? Contact our team."
        emails={[{ label: "Technical Support", address: EMAILS.support }]}
      />
    </div>
  );
}
